export const MAX_EVIDENCE_FILE_SIZE = 25 * 1024 * 1024;
const MAX_PHOTO_DIMENSION = 1920;
const PHOTO_OUTPUT_QUALITY = 0.82;
const MIN_TEXT_COMPRESSION_SIZE = 4 * 1024;

const PHOTO_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);

const DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const DOCUMENT_EXTENSIONS = new Set([
  'pdf',
  'txt',
  'csv',
  'docx',
  'xlsx',
]);

const MIME_TYPE_BY_EXTENSION = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export class EvidenceFileError extends Error {
  constructor(message, code = 'EVIDENCE_FILE_INVALID') {
    super(message);
    this.name = 'EvidenceFileError';
    this.code = code;
  }
}

function createEvidenceId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function fileExtension(fileName) {
  return String(fileName || '').split('.').pop()?.toLowerCase() || '';
}

export function sanitizeEvidenceFileName(fileName) {
  const normalized = String(fileName || 'evidencia')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');

  return normalized.slice(-120) || 'evidencia';
}

export function formatEvidenceFileSize(size) {
  const bytes = Number(size || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateEvidenceFile(file, requestedType) {
  if (!file) throw new EvidenceFileError('Nenhum arquivo foi selecionado.');
  if (file.size <= 0) throw new EvidenceFileError(`${file.name}: o arquivo está vazio.`);
  if (file.size > MAX_EVIDENCE_FILE_SIZE) {
    throw new EvidenceFileError(`${file.name}: o limite por arquivo é 25 MB.`, 'EVIDENCE_FILE_TOO_LARGE');
  }

  const mimeType = String(file.type || '').toLowerCase();
  const extension = fileExtension(file.name);

  if (requestedType === 'foto' && !PHOTO_MIME_TYPES.has(mimeType)) {
    throw new EvidenceFileError(`${file.name}: use foto JPEG, PNG, WebP, HEIC ou HEIF.`, 'EVIDENCE_PHOTO_TYPE');
  }

  if (requestedType === 'documento'
      && !DOCUMENT_MIME_TYPES.has(mimeType)
      && !DOCUMENT_EXTENSIONS.has(extension)) {
    throw new EvidenceFileError(`${file.name}: formato de documento não permitido.`, 'EVIDENCE_DOCUMENT_TYPE');
  }

  return true;
}

export async function calculateSha256(file) {
  if (!globalThis.crypto?.subtle) {
    throw new EvidenceFileError('Este navegador não oferece verificação SHA-256.', 'EVIDENCE_HASH_UNAVAILABLE');
  }

  const buffer = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

function replaceExtension(fileName, extension) {
  const normalized = String(fileName || 'foto').replace(/\.[^.]+$/, '');
  return `${normalized}.${extension}`;
}

async function loadImageSource(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Alguns WebViews nao decodificam HEIC via createImageBitmap. Tentamos o elemento img.
    }
  }

  if (typeof Image === 'undefined') {
    throw new EvidenceFileError('Este dispositivo n\u00e3o oferece compacta\u00e7\u00e3o de imagens.', 'EVIDENCE_IMAGE_COMPRESSION_UNAVAILABLE');
  }

  const imageUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = imageUrl;
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });
    return image;
  } catch {
    throw new EvidenceFileError(
      `${file.name}: este navegador n\u00e3o conseguiu compactar a foto. Converta-a para JPEG, PNG ou WebP e tente novamente.`,
      'EVIDENCE_IMAGE_DECODE_FAILED'
    );
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new EvidenceFileError('N\u00e3o foi poss\u00edvel compactar a foto selecionada.', 'EVIDENCE_IMAGE_COMPRESSION_FAILED'));
    }, mimeType, quality);
  });
}

async function compressPhoto(file) {
  if (typeof document === 'undefined') return {
    file,
    compression: 'original',
    originalMimeType: file.type,
  };

  const source = await loadImageSource(file);
  const width = source.width || source.naturalWidth;
  const height = source.height || source.naturalHeight;
  const scale = Math.min(1, MAX_PHOTO_DIMENSION / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext('2d', { alpha: false });

  if (!context) {
    if (typeof source.close === 'function') source.close();
    throw new EvidenceFileError('Este dispositivo n\u00e3o conseguiu preparar a foto.', 'EVIDENCE_CANVAS_UNAVAILABLE');
  }

  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (typeof source.close === 'function') source.close();

  const preferredMimeType = typeof canvas.toDataURL === 'function'
    && canvas.toDataURL('image/webp').startsWith('data:image/webp')
    ? 'image/webp'
    : 'image/jpeg';
  const blob = await canvasToBlob(canvas, preferredMimeType, PHOTO_OUTPUT_QUALITY);
  if (blob.size >= file.size && file.type !== 'image/heic' && file.type !== 'image/heif') {
    return { file, compression: 'original-menor', originalMimeType: file.type };
  }

  return {
    file: new File([blob], replaceExtension(file.name, preferredMimeType === 'image/webp' ? 'webp' : 'jpg'), {
      type: preferredMimeType,
      lastModified: file.lastModified || Date.now(),
    }),
    compression: preferredMimeType === 'image/webp' ? 'webp-82' : 'jpeg-82',
    originalMimeType: file.type,
  };
}

async function compressTextDocument(file) {
  const mimeType = String(file.type || MIME_TYPE_BY_EXTENSION[fileExtension(file.name)] || '').toLowerCase();
  const isCompressibleText = mimeType === 'text/plain' || mimeType === 'text/csv';

  if (!isCompressibleText || file.size < MIN_TEXT_COMPRESSION_SIZE || typeof CompressionStream === 'undefined') {
    return {
      file,
      compression: isCompressibleText ? 'original' : 'formato-compactado',
      originalMimeType: mimeType || file.type,
    };
  }

  const compressedStream = file.stream().pipeThrough(new CompressionStream('gzip'));
  const blob = await new Response(compressedStream).blob();
  if (blob.size >= file.size) {
    return { file, compression: 'original-menor', originalMimeType: mimeType };
  }

  return {
    file: new File([blob], `${file.name}.gz`, {
      type: 'application/gzip',
      lastModified: file.lastModified || Date.now(),
    }),
    compression: 'gzip',
    originalMimeType: mimeType,
  };
}

async function optimizeEvidenceFile(file, requestedType) {
  return requestedType === 'foto' ? compressPhoto(file) : compressTextDocument(file);
}

export async function prepareEvidenceFiles(files, requestedType) {
  const selectedFiles = Array.from(files || []);
  const prepared = [];

  for (const file of selectedFiles) {
    validateEvidenceFile(file, requestedType);
    const optimized = await optimizeEvidenceFile(file, requestedType);
    const optimizedFile = optimized.file;
    if (optimizedFile.size > MAX_EVIDENCE_FILE_SIZE) {
      throw new EvidenceFileError(`${file.name}: o arquivo compactado ainda ultrapassa 25 MB.`, 'EVIDENCE_FILE_TOO_LARGE');
    }
    const hashSha256 = await calculateSha256(optimizedFile);
    const mimeType = optimizedFile.type || MIME_TYPE_BY_EXTENSION[fileExtension(optimizedFile.name)] || 'application/octet-stream';
    prepared.push({
      id: createEvidenceId(),
      type: requestedType,
      file: optimizedFile,
      name: file.name,
      safeName: sanitizeEvidenceFileName(optimizedFile.name),
      mimeType,
      originalMimeType: optimized.originalMimeType || file.type || mimeType,
      originalSize: file.size,
      originalSizeLabel: formatEvidenceFileSize(file.size),
      compression: optimized.compression,
      size: optimizedFile.size,
      sizeLabel: formatEvidenceFileSize(optimizedFile.size),
      hashSha256,
      previewUrl: requestedType === 'foto' ? URL.createObjectURL(optimizedFile) : null,
      capturedAt: new Date(file.lastModified || Date.now()).toISOString(),
    });
  }

  return prepared;
}

export function releaseEvidencePreview(evidence) {
  if (evidence?.previewUrl) URL.revokeObjectURL(evidence.previewUrl);
}
